import React from 'react'
import { Button, Element, Icon, Tag } from 'react-bulma-components'
import { DeleteIcon, EditIcon, InfoIcon, WorkPeriodTagIcon } from '../icons'
import { useModal } from '../modal'
import Modal from '../modal'
import {
  ApplyTagsToMultipleForm,
  ApplyTagsToMultipleFormProps,
} from './forms/work-period-tags-form'
import { WorkPeriodTag } from '../../api/work-period-tags'

interface WorkPeriodTagsProps {
  tags: WorkPeriodTag[]
  isEditable?: boolean
  onTagDelete?: (tagId: string) => void
  formProps?: Omit<ApplyTagsToMultipleFormProps, 'tags'>
  size?: 'small' | 'medium'
  hideEmptyMessage?: boolean
}

/**
 * Component to display work period tags
 * Shows tags in a Tag.Group with info color and medium size
 * Displays "Aucun tag" when no tags are present
 * When isEditable=true, shows an edit button and allows tag deletion with confirmation
 * Manages the edit modal internally when isEditable is true
 */
const WorkPeriodTags: React.FC<WorkPeriodTagsProps> = ({
  tags,
  isEditable = false,
  onTagDelete,
  formProps,
  size = 'medium',
  hideEmptyMessage = false,
}) => {
  const [pendingDeleteTagId, setPendingDeleteTagId] = React.useState<string | null>(null)
  const [hoveredTagId, setHoveredTagId] = React.useState<string | null>(null)
  const editTagsModal = useModal()

  const hasTags = tags.length > 0
  const canDeleteTags = isEditable && onTagDelete

  const handleEditClick = () => {
    if (isEditable) {
      editTagsModal.setIsDisplayed(true)
    }
  }

  /**
   * Handle form cancellation - close the modal
   */
  const handleCancel = () => {
    editTagsModal.setIsDisplayed(false)
  }

  const handleTagClick = (tagId: string) => {
    if (!canDeleteTags) return

    if (pendingDeleteTagId === tagId) {
      // Second click: confirm deletion
      onTagDelete?.(tagId)
      setPendingDeleteTagId(null)
    } else {
      // First click: show confirmation
      setPendingDeleteTagId(tagId)
    }
  }

  // Reset pending delete when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setPendingDeleteTagId(null)
    }

    if (pendingDeleteTagId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
    return undefined
  }, [pendingDeleteTagId])

  return (
    <Element
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
      data-test="work-period-tags-container"
    >
      <Tag.Group>
        {!hasTags && !hideEmptyMessage && (
          <Tag textColor="grey" size={size}>
            <Icon.Text>
              <Icon size="small" mt={1} mr={1}>
                <InfoIcon />
              </Icon>
              <Element textColor="grey" renderAs="i">
                Aucune catégorie
              </Element>
            </Icon.Text>
          </Tag>
        )}
        {hasTags &&
          tags.map(tag => {
            const isPendingDelete = pendingDeleteTagId === tag._id
            const isHovered = hoveredTagId === tag._id && canDeleteTags && !isPendingDelete
            return (
              <Tag
                key={tag._id}
                size={size}
                color={isPendingDelete ? 'danger' : undefined}
                style={{
                  cursor: canDeleteTags ? 'pointer' : 'default',
                  opacity: isPendingDelete ? 0.7 : 1,
                  transition: 'background-color 0.2s',
                }}
                onClick={e => {
                  e.stopPropagation()
                  handleTagClick(tag._id)
                }}
                onMouseEnter={() => setHoveredTagId(tag._id)}
                onMouseLeave={() => setHoveredTagId(null)}
              >
                <Icon.Text>
                  {isHovered && (
                    <Icon size="small" mt={1}>
                      <DeleteIcon />
                    </Icon>
                  )}
                  <Icon size="small" mt={1} mr={1}>
                    {isPendingDelete ? <DeleteIcon /> : <WorkPeriodTagIcon />}
                  </Icon>
                  {isPendingDelete ? (
                    <>
                      <Element renderAs="i">
                        Cliquer pour confirmer la suppression de{' '}
                        <Element renderAs="strong" textColor="black">
                          {tag.name}
                        </Element>
                      </Element>
                    </>
                  ) : (
                    tag.name
                  )}
                </Icon.Text>
              </Tag>
            )
          })}
        {isEditable && (
          <Button
            size="medium"
            color="ghost"
            textSize={7}
            py={4}
            onClick={handleEditClick}
            style={{ cursor: 'pointer' }}
            textColor="link"
            data-test="work-period-add-tags-button"
            type="button"
          >
            <Icon.Text>
              <Icon size="small" mt={1} mr={1}>
                <EditIcon />
              </Icon>
              <Element renderAs="i">Ajouter</Element>
            </Icon.Text>
          </Button>
        )}
      </Tag.Group>
      {isEditable && (
        <Modal title="Ajouter des catégories" actions={editTagsModal} maxWidth={600}>
          {editTagsModal.isDisplayed && (
            <ApplyTagsToMultipleForm
              {...formProps}
              tags={tags}
              onSubmit={params => {
                formProps?.onSubmit(params)
                editTagsModal.setIsDisplayed(false)
              }}
              onCancel={handleCancel}
            />
          )}
        </Modal>
      )}
    </Element>
  )
}

export default WorkPeriodTags
